package fr.frux.core.ads

import fr.frux.core.common.entitlements.AccountEntitlements
import fr.frux.core.common.entitlements.EntitlementsHolder
import fr.frux.core.common.entitlements.EntitlementsRefresher
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.result.runCatchingCancellable
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlin.coroutines.ContinuationInterceptor
import kotlin.coroutines.EmptyCoroutineContext
import kotlin.time.Duration
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds

/**
 * Seule implémentation de la politique (ADR 0030, #527). L'éligibilité se calcule à un seul endroit,
 * [recompute] :
 *
 * ```
 * eligible = supported && enabled && droits résolus && pas sans pub && session >= 2 &&
 *     aucun hold && consentement résolu avec canRequestAds && SDK initialisé
 * ```
 *
 * Rien n'est demandé à [platform] (ni UMP ni la régie) tant que la « porte » n'est pas ouverte : la
 * même condition sans les holds autres qu'onboarding, sans le consentement et sans le SDK. Un compte
 * Frux+ qui démarre à froid hors ligne (droits non résolus) ne contacte donc personne.
 *
 * Droits sans pub : décomptés depuis l'heure du serveur ancrée en temps monotone
 * ([AccountEntitlements.serverClock]), jamais depuis l'horloge de l'appareil. Une minuterie relance
 * [recompute] à l'échéance d'un `AD_FREE` ou d'un premium daté.
 *
 * Fraîcheur des droits : [refresher] relit `/auth/me` à chaque nouvelle session (le démarrage du
 * process est couvert par la restauration de session), et la porte reste fermée pendant la
 * relecture, pour qu'un octroi fait pendant l'absence ne laisse pas partir de requête UMP. Un
 * premium daté échu n'est pas pris pour une fin d'abonnement : il a pu être renouvelé, la porte
 * reste fermée et une relecture est demandée (une par session). Un `AD_FREE` échu, lui, rouvre.
 *
 * Interstitiel (#528) : préchargé en tâche de fond dès que [AdsState.eligible] est vrai, une seule
 * tentative de chargement par session. [maybeShowInterstitial] ne suspend jamais et ne montre que
 * ce qui est déjà prêt. Pendant l'annonce, une claim [AdPlacement.INTERSTITIAL] fait taire les
 * autres emplacements.
 *
 * Vidéo récompensée (#531) : proposée quand la publicité est éligible et le compte connu
 * ([canOfferRewarded]), jouée sous une claim [AdPlacement.REWARDED] qui l'emporte sur l'interstitiel.
 * Le droit `AD_FREE` vient du serveur, jamais d'ici.
 *
 * Classe volontairement unique et grosse (`TooManyFunctions` supprimé) : seul lieu de la politique
 * publicitaire, la découper disperserait l'éligibilité.
 *
 * À appeler depuis le thread principal, comme [scope], qui doit vivre aussi longtemps que l'app.
 */
@Suppress("TooManyFunctions")
class DefaultAdsController(
    private val platform: AdsPlatform,
    private val entitlements: EntitlementsHolder,
    private val refresher: EntitlementsRefresher,
    private val sessions: AdSessionTracker,
    config: AdsConfig,
    private val scope: CoroutineScope,
) : AdsController {
    private val supported = platform.supported && config.enabled

    private val _state = MutableStateFlow(AdsState.UNSUPPORTED.copy(supported = supported))
    override val state: StateFlow<AdsState> = _state.asStateFlow()

    // Vidéo récompensée (#531). Offre recalculée dans recompute(), seul lieu de l'éligibilité.
    private val rewardedConfigured = config.rewardedUnitId.isNotEmpty()
    private val _canOfferRewarded = MutableStateFlow(false)
    override val canOfferRewarded: StateFlow<Boolean> = _canOfferRewarded.asStateFlow()
    private var rewardedInProgress = false
    private val _rewardedPending = MutableStateFlow(false)
    override val rewardedPending: StateFlow<Boolean> = _rewardedPending.asStateFlow()
    private var pendingAccountId: Long? = null
    private var pendingTimer: Job? = null

    // Jetons par identité : deux holds de même raison se libèrent indépendamment.
    private val holds = mutableListOf<HoldToken>()
    private val claims = mutableListOf<ClaimToken>()

    private var nextClaimId = 0L

    private var sdkReady = false

    // Dernière exigence d'UMP. Publiée seulement porte ouverte : un compte devenu sans pub ne voit
    // plus l'entrée du Profil, et openPrivacyOptions() ne contacte plus UMP.
    private var umpPrivacyOptionsRequired = false
    private var consentJob: Job? = null
    private var initJob: Job? = null
    private var initFailedInSession = 0
    private var expiryTimer: Job? = null
    private var refreshJob: Job? = null
    private var lapsedRefreshSession = 0

    // Interstitiel. `interstitialShownSession` n'est connu qu'après la lecture des préférences, faite
    // avant tout préchargement : tant qu'elle manque, aucune annonce n'est prête.
    private var interstitialShownSession: Int? = null
    private var interstitialReady = false
    private var interstitialShowing = false
    private var interstitialJob: Job? = null
    private var interstitialLoadSession = 0
    private var sensitiveFlowEnded = false

    // Un process démarré sans interface (push FCM) part de « pas au premier plan » : ce n'est pas
    // un départ de la personne, il ne date aucun passage en arrière-plan.
    private var foregroundSeen = false

    init {
        if (supported) {
            scope.launch {
                platform.foreground.distinctUntilChanged().collect { foreground -> onForegroundChanged(foreground) }
            }
            scope.launch {
                entitlements.state.collect {
                    recompute()
                    scheduleAdFreeExpiry(it)
                }
            }
        }
    }

    override fun hold(reason: AdHold): AdHandle {
        val token = HoldToken(reason)
        holds += token
        recompute()
        return AdHandle {
            if (holds.remove(token)) {
                if (reason == AdHold.SENSITIVE_FLOW) sensitiveFlowEnded = true
                recompute()
            }
        }
    }

    override fun maybeShowInterstitial(trigger: InterstitialTrigger): Boolean {
        // Consommée par le premier appel qui suit un flux sensible, qu'il montre ou non.
        val justAfterSensitiveFlow = sensitiveFlowEnded
        sensitiveFlowEnded = false
        val session = _state.value.sessionIndex
        val allowed =
            _state.value.eligible &&
                !justAfterSensitiveFlow &&
                !interstitialShowing &&
                !rewardedInProgress &&
                interstitialReady &&
                interstitialShownSession != session
        val shown = allowed && showInterstitial(session)
        if (shown) {
            Logger.i(TAG, "Interstitial shown: $trigger, session=$session")
        } else {
            Logger.i(TAG, "Interstitial skipped: $trigger, afterSensitiveFlow=$justAfterSensitiveFlow")
        }
        return shown
    }

    private fun showInterstitial(session: Int): Boolean {
        val claim = claim(AdPlacement.INTERSTITIAL)
        interstitialShowing = true
        interstitialReady = false
        val shown =
            runCatching { platform.showInterstitial { endInterstitial(claim) } }
                .getOrElse { error ->
                    Logger.e(TAG, "Interstitial show failed", error)
                    false
                }
        if (shown) {
            // Consigné dès l'affichage : une mort du process pendant l'annonce ne réarme pas la session.
            interstitialShownSession = session
            scope.launch { sessions.markInterstitialShown(session) }
        } else {
            endInterstitial(claim)
            // L'annonce n'a pas pu partir (expirée, pas d'Activity) : un nouveau chargement est permis.
            interstitialLoadSession = 0
            recompute()
        }
        return shown
    }

    private fun endInterstitial(claim: AdClaim) {
        if (!interstitialShowing) return
        interstitialShowing = false
        claim.release()
    }

    /**
     * L'arbitrage se fait par jeton et non par type : deux claims du même emplacement (deux
     * compositions d'une même bannière) ne s'affichent jamais ensemble, seule celle dont
     * [AdClaim.id] vaut [AdsState.visibleClaimId] le peut.
     */
    override fun claim(placement: AdPlacement): AdClaim {
        val token = ClaimToken(nextClaimId++, placement)
        claims += token
        recompute()
        return AdClaim(token.id) { if (claims.remove(token)) recompute() }
    }

    override fun onConsentIntroContinue() {
        if (_state.value.consent != AdConsent.IntroPending) return
        _state.update { it.copy(consent = AdConsent.FormShowing) }
        consentJob =
            scope.launch {
                val consent =
                    runCatchingCancellable { AdConsent.Resolved(platform.showConsentForm()) }
                        .getOrElse { error ->
                            Logger.e(TAG, "Consent form failed", error)
                            AdConsent.Error
                        }
                _state.update { it.copy(consent = consent) }
                recompute()
            }
    }

    override fun onConsentIntroDismissed() {
        if (_state.value.consent != AdConsent.IntroPending) return
        Logger.i(TAG, "Consent intro dismissed, ads closed until next session")
        _state.update { it.copy(consent = AdConsent.Error) }
        recompute()
    }

    override suspend fun openPrivacyOptions(): Boolean {
        if (!_state.value.privacyOptionsAvailable) return false
        val result = runCatchingCancellable { platform.showPrivacyOptions() }
        // L'appelant (un écran) peut tourner ailleurs que le contrôleur : l'état mutable n'est
        // touché que sur le dispatcher de son scope.
        withContext(scope.coroutineContext[ContinuationInterceptor] ?: EmptyCoroutineContext) {
            result
                .onSuccess { canRequestAds ->
                    _state.update { it.copy(consent = AdConsent.Resolved(canRequestAds)) }
                    recompute()
                }.onFailure { error -> Logger.e(TAG, "Privacy options form failed", error) }
        }
        return result.isSuccess
    }

    override suspend fun watchRewardedForAdFree(): RewardedResult =
        // L'appelant (un ViewModel) peut tourner ailleurs que le contrôleur : l'état mutable n'est
        // touché que sur le dispatcher de son scope, comme openPrivacyOptions().
        withContext(scope.coroutineContext[ContinuationInterceptor] ?: EmptyCoroutineContext) {
            val accountId = entitlements.state.value?.accountId
            when {
                // Second appui pendant la vidéo : rien de plus, et rien à dire.
                rewardedInProgress -> RewardedResult.DISMISSED

                !_canOfferRewarded.value || accountId == null -> RewardedResult.UNAVAILABLE

                else -> playRewarded(accountId)
            }
        }

    private suspend fun playRewarded(accountId: Long): RewardedResult {
        rewardedInProgress = true
        // La claim n'est posée qu'une fois la vidéo chargée : pendant le chargement (jusqu'à plusieurs
        // secondes) la bannière et l'annonce native restent en place, rien ne saute sous le doigt.
        var claim: AdClaim? = null
        return try {
            val loaded =
                runCatchingCancellable { platform.loadRewarded() }
                    .getOrElse { error ->
                        Logger.e(TAG, "Rewarded load failed", error)
                        false
                    }
            val outcome =
                if (!loaded) {
                    RewardedOutcome.UNAVAILABLE
                } else {
                    claim = claim(AdPlacement.REWARDED)
                    val request = RewardedRequest(accountId.toString(), REWARD_KIND_AD_FREE)
                    runCatchingCancellable { platform.showRewarded(request) }
                        .getOrElse { error ->
                            Logger.e(TAG, "Rewarded show failed", error)
                            RewardedOutcome.UNAVAILABLE
                        }
                }
            Logger.i(TAG, "Rewarded outcome: $outcome")
            when (outcome) {
                RewardedOutcome.EARNED -> {
                    consumeInterstitialOfSession()
                    // Dans le scope du contrôleur : dépiler l'écran appelant n'annule ni la fenêtre
                    // d'attente ni la relecture à 30 s.
                    scope.async { awaitAdFreeGrant(accountId) }.await()
                }

                RewardedOutcome.DISMISSED -> {
                    RewardedResult.DISMISSED
                }

                RewardedOutcome.UNAVAILABLE -> {
                    RewardedResult.UNAVAILABLE
                }
            }
        } finally {
            rewardedInProgress = false
            claim?.release()
            recompute()
        }
    }

    /** Une vidéo vue jusqu'au bout tient lieu de l'interstitiel de la session (décision #531). */
    private fun consumeInterstitialOfSession() {
        val session = _state.value.sessionIndex
        interstitialShownSession = session
        interstitialReady = false
        scope.launch { sessions.markInterstitialShown(session) }
    }

    /**
     * Le droit vient du rappel SSV, que Google envoie au serveur en quelques secondes : relectures de
     * `/auth/me` à 1, 2, 3 puis 4 s. L'octroi se juge à l'heure du serveur ([adFreeRemaining]),
     * jamais à l'horloge de l'appareil. Pas encore vu : une dernière relecture à 30 s, hors de
     * l'appelant, pour que la publicité disparaisse sans attendre la session suivante.
     */
    private suspend fun awaitAdFreeGrant(accountId: Long): RewardedResult {
        repeat(GRANT_READS) {
            delay(GRANT_READ_INTERVAL)
            runCatchingCancellable { refresher.refresh() }
                .onFailure { error -> Logger.e(TAG, "Entitlements refresh failed", error) }
            if (entitlements.state.value?.adFreeRemaining() != null) return RewardedResult.GRANTED
        }
        scope.launch {
            delay(LATE_GRANT_READ)
            runCatchingCancellable { refresher.refresh() }
                .onFailure { error -> Logger.e(TAG, "Entitlements refresh failed", error) }
        }
        openPendingWindow(accountId)
        return RewardedResult.PENDING
    }

    /**
     * Vidéo vue sans octroi visible : le rappel SSV peut tarder. On n'en redemande pas une autre
     * avant [PENDING_WINDOW], ce qui serait déroutant, et la fenêtre vit ici pour survivre à la
     * navigation. L'échéance suit la même minuterie virtuelle que [scheduleAdFreeExpiry].
     */
    private fun openPendingWindow(accountId: Long) {
        // Compte changé pendant la relecture : la vidéo n'était pas la sienne.
        if (entitlements.state.value?.accountId != accountId) return
        pendingAccountId = accountId
        _rewardedPending.value = true
        pendingTimer?.cancel()
        pendingTimer =
            scope.launch {
                delay(PENDING_WINDOW)
                // Ne pas s'annuler soi-même depuis closePendingWindow().
                pendingTimer = null
                closePendingWindow()
                recompute()
            }
    }

    private fun pendingStillRelevant(rights: AccountEntitlements?): Boolean =
        rights != null && rights.accountId == pendingAccountId && !rights.premium && rights.adFreeRemaining() == null

    private fun closePendingWindow() {
        pendingTimer?.cancel()
        pendingTimer = null
        pendingAccountId = null
        _rewardedPending.value = false
    }

    private suspend fun onForegroundChanged(foreground: Boolean) {
        if (!foreground) {
            if (foregroundSeen) sessions.onBackground()
            return
        }
        val processStart = !foregroundSeen
        foregroundSeen = true
        val index = sessions.onForeground()
        if (index == _state.value.sessionIndex) return
        sensitiveFlowEnded = false
        _state.update { current ->
            // Une session neuve retente ce qui n'a pas abouti (erreur, écran écarté ou laissé en
            // plan). Un formulaire ouvert le reste, sa réponse tranchera. Un consentement résolu
            // tient pour la vie du process, UMP le relit au démarrage suivant.
            val consent =
                when (current.consent) {
                    AdConsent.Error, AdConsent.IntroPending -> AdConsent.Unknown
                    else -> current.consent
                }
            current.copy(sessionIndex = index, consent = consent)
        }
        // Au démarrage du process, SessionStore.restore() vient de lire /auth/me.
        if (!processStart) refreshEntitlements("new session")
        recompute()
    }

    private fun recompute() {
        val current = _state.value
        val gateOpen =
            supported &&
                rightsAllowAds(current.sessionIndex) &&
                current.sessionIndex >= 2 &&
                holds.none { it.reason == AdHold.ONBOARDING }

        if (gateOpen) {
            advanceConsent(current.consent)
        } else if (current.consent == AdConsent.IntroPending) {
            // Porte refermée avant que l'écran d'information ne soit tranché (droits devenus sans
            // pub, onboarding) : on ne le montre pas, et on le reposera si elle se rouvre.
            _state.update { it.copy(consent = AdConsent.Unknown) }
        } else if (current.consent == AdConsent.Unknown && consentJob?.isActive == true) {
            // Requête UMP en vol (ou encore en attente d'une Activity) quand la porte se ferme :
            // annulée, pour que sa réponse n'ouvre ni l'écran d'information ni la régie. Le port
            // revérifie l'annulation après l'attente d'Activity, avant de contacter UMP. Un
            // formulaire ouvert (FormShowing) n'est jamais annulé, la personne y répond.
            consentJob?.cancel()
            consentJob = null
        }

        val consent = _state.value.consent
        val eligible =
            gateOpen && holds.isEmpty() && consent is AdConsent.Resolved && consent.canRequestAds && sdkReady
        preloadInterstitial(eligible, current.sessionIndex)
        val winner = if (eligible) claims.minWithOrNull(CLAIM_ORDER) else null
        _state.update {
            it.copy(
                eligible = eligible,
                visiblePlacement = winner?.placement,
                visibleClaimId = winner?.id,
                privacyOptionsAvailable = gateOpen && umpPrivacyOptionsRequired,
            )
        }
        updateRewardedOffer(eligible)
    }

    /**
     * Droits connus, frais et sans pub. Un premium daté échu demande une relecture (une par
     * session, pour ne pas boucler si le serveur tarde à le clore) et ferme la porte en attendant.
     */
    private fun rightsAllowAds(session: Int): Boolean {
        val rights = entitlements.state.value ?: return false
        val lapsed = rights.premiumLapsed()
        if (supported && lapsed && lapsedRefreshSession != session) {
            lapsedRefreshSession = session
            refreshEntitlements("premium expired")
        }
        return !lapsed && refreshJob?.isActive != true && rights.adFreeRemaining() == null
    }

    /** Fait avancer le consentement d'un pas quand la porte est ouverte. */
    private fun advanceConsent(consent: AdConsent) {
        when {
            consent == AdConsent.Unknown && consentJob?.isActive != true -> requestConsentInfo()
            consent is AdConsent.Resolved && consent.canRequestAds && !sdkReady -> initializeSdk()
        }
    }

    private fun requestConsentInfo() {
        consentJob =
            scope.launch {
                val info =
                    runCatchingCancellable { platform.requestConsentInfo() }
                        .getOrElse { error ->
                            Logger.e(TAG, "Consent info update failed", error)
                            null
                        }
                _state.update {
                    it.copy(
                        consent =
                            when {
                                info == null -> AdConsent.Error

                                // Aucun écran de choix ne suit : pas d'écran d'information non plus.
                                !info.formRequired -> AdConsent.Resolved(info.canRequestAds)

                                else -> AdConsent.IntroPending
                            },
                    )
                }
                if (info != null) umpPrivacyOptionsRequired = info.privacyOptionsRequired
                recompute()
            }
    }

    private fun updateRewardedOffer(eligible: Boolean) {
        val rights = entitlements.state.value
        // Fenêtre d'attente : fermée dès que le droit est visible, ou que le compte change/part.
        if (_rewardedPending.value && !pendingStillRelevant(rights)) closePendingWindow()
        _canOfferRewarded.value =
            eligible && rewardedConfigured && !rewardedInProgress && !_rewardedPending.value &&
            rights?.accountId != null
    }

    private fun preloadInterstitial(
        eligible: Boolean,
        session: Int,
    ) {
        if (!eligible || interstitialLoadSession == session) return
        if (interstitialReady || interstitialShowing || interstitialJob?.isActive == true) return
        interstitialLoadSession = session
        interstitialJob =
            scope.launch {
                runCatchingCancellable {
                    val shown = sessions.interstitialShownSession()
                    interstitialShownSession = shown
                    if (shown != session) platform.preloadInterstitial() else false
                }.onSuccess { interstitialReady = it }
                    .onFailure { error -> Logger.e(TAG, "Interstitial preload failed", error) }
            }
    }

    private fun initializeSdk() {
        val session = _state.value.sessionIndex
        if (initJob?.isActive == true || initFailedInSession == session) return
        initJob =
            scope.launch {
                runCatchingCancellable { platform.initialize() }
                    .onSuccess { sdkReady = true }
                    .onFailure { error ->
                        // Une seule tentative par session : recompute() est appelé à chaque hold.
                        initFailedInSession = session
                        Logger.e(TAG, "Ads SDK initialization failed", error)
                    }
                recompute()
            }
    }

    private fun refreshEntitlements(reason: String) {
        if (refreshJob?.isActive == true) return
        Logger.i(TAG, "Entitlements refresh: $reason")
        refreshJob =
            scope.launch {
                runCatchingCancellable { refresher.refresh() }
                    .onFailure { error -> Logger.e(TAG, "Entitlements refresh failed", error) }
                // Hors du job : recompute() lit refreshJob, qui doit être fini.
                scope.launch { recompute() }
            }
    }

    private fun scheduleAdFreeExpiry(rights: AccountEntitlements?) {
        expiryTimer?.cancel()
        if (rights?.adFreeRemaining() == null) return
        if (rights.serverClock == null) {
            Logger.i(TAG, "Ad-free rights without server time, ads stay off until the next /auth/me")
        }
        expiryTimer =
            scope.launch {
                // En boucle : l'arrondi de l'horloge monotone peut réveiller un poil avant l'échéance,
                // et premium et AD_FREE n'échoient pas forcément ensemble.
                var remaining = rights.adFreeRemaining()
                while (remaining != null && remaining.isFinite()) {
                    delay(remaining)
                    recompute()
                    remaining = rights.adFreeRemaining()
                }
            }
    }

    private class HoldToken(
        val reason: AdHold,
    )

    private class ClaimToken(
        val id: Long,
        val placement: AdPlacement,
    )

    private companion object {
        const val TAG = "Ads"

        /** Type de récompense relayé au serveur dans `custom_data` (#531). */
        const val REWARD_KIND_AD_FREE = "AD_FREE"
        const val GRANT_READS = 4
        val GRANT_READ_INTERVAL = 1.seconds
        val LATE_GRANT_READ = 30.seconds

        /** Attente après un PENDING avant de reproposer une vidéo (#531). */
        val PENDING_WINDOW = 5.minutes

        /**
         * Gagnant de l'arbitrage, déterministe : priorité la plus haute, puis à priorité égale la
         * vidéo récompensée devant l'interstitiel (elle répond à un geste explicite de la personne,
         * ADR 0030 point 2), puis la claim la plus ancienne.
         */
        val CLAIM_ORDER: Comparator<ClaimToken> =
            compareByDescending<ClaimToken> { it.placement.priority }
                .thenBy { if (it.placement == AdPlacement.REWARDED) 0 else 1 }
                .thenBy { it.id }
    }
}

/**
 * Premium daté dont l'échéance est passée à l'heure du serveur, sans relecture depuis : il a pu être
 * renouvelé, on ne le prend pas pour une fin d'abonnement (une relecture de `/auth/me` tranchera).
 */
internal fun AccountEntitlements.premiumLapsed(): Boolean {
    val now = serverClock?.now()
    val expiresAt = premiumExpiresAt
    return premium && now != null && expiresAt != null && now >= expiresAt
}

/**
 * Temps restant sans publicité à l'heure du serveur, `null` si la publicité est permise.
 * [Duration.INFINITE] pour un droit sans fin, et aussi quand l'heure du serveur est inconnue : le
 * statut rendu par le serveur fait alors foi jusqu'au prochain `/auth/me`, plutôt que l'horloge de
 * l'appareil (qui montrerait la pub à un abonné dont le téléphone avance).
 */
internal fun AccountEntitlements.adFreeRemaining(): Duration? {
    val now = serverClock?.now()
    return when {
        now == null -> {
            Duration.INFINITE.takeIf { premium || adFreeUntil != null }
        }

        !isAdFreeAt(now) -> {
            null
        }

        else -> {
            val premiumLeft =
                if (isPremiumAt(now)) premiumExpiresAt?.let { it - now } ?: Duration.INFINITE else Duration.ZERO
            val adFreeLeft = adFreeUntil?.takeIf { now < it }?.let { it - now } ?: Duration.ZERO
            maxOf(premiumLeft, adFreeLeft)
        }
    }
}
